"use client";

interface Props {
  activeTab: string;
  onTabChange: (tab: string) => void;
  onChatOpen: () => void;
  onSettingsOpen: () => void;
  onHelpOpen: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

const NAV_ITEMS = [
  { id: "present", label: "Today", icon: "M12 2l2.09 6.26L20.18 9l-5 4.27L16.82 20 12 16.47 7.18 20l1.64-6.73L3.82 9l6.09-.74L12 2z" },
  { id: "schedule", label: "Plan", icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" },
  { id: "decide", label: "Decide", icon: "M12 3v2m0 14v2M5.636 5.636l1.414 1.414m9.9 9.9l1.414 1.414M3 12h2m14 0h2M5.636 18.364l1.414-1.414m9.9-9.9l1.414-1.414 M16 12a4 4 0 11-8 0 4 4 0 018 0z" },
  { id: "life", label: "Reflect", icon: "M12 22c0-4-3-6-3-10a3 3 0 016 0c0 4-3 6-3 10z M7 14c-1.5-1-3-3.5-3-6a8 8 0 0116 0c0 2.5-1.5 5-3 6" },
];

const HELP_ICON = (
  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
    <path d="M12 17h.01" />
  </svg>
);

const SETTINGS_ICON = (
  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
  </svg>
);

export default function DesktopSidebar({
  activeTab,
  onTabChange,
  onChatOpen,
  onSettingsOpen,
  onHelpOpen,
  collapsed,
  onToggleCollapsed,
}: Props) {
  return (
    <aside
      className={`h-app fixed left-0 top-0 z-40 hidden flex-col border-r border-border bg-card transition-[width] duration-200 ease-out lg:flex ${
        collapsed ? "w-16" : "w-60"
      }`}
    >
      {/* Logo + Collapse toggle */}
      <div
        className={`border-b border-border flex items-center ${
          collapsed ? "px-2 py-3 flex-col gap-2" : "px-5 py-5 justify-between"
        }`}
      >
        <div className={`flex items-center ${collapsed ? "" : "gap-2.5"}`}>
          <div className="w-8 h-8 rounded-lg bg-forest flex items-center justify-center shrink-0">
            <span className="text-white text-xs font-bold">DE</span>
          </div>
          {!collapsed && (
            <span className="text-sm font-semibold text-text">DecisionEase</span>
          )}
        </div>
        <button
          onClick={onToggleCollapsed}
          data-testid="desktop-sidebar-toggle"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={`flex items-center justify-center rounded-lg text-muted hover:text-text hover:bg-secondary/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-med focus-visible:ring-offset-2 ${
            collapsed ? "h-9 w-9" : "h-10 w-10"
          }`}
        >
          <svg
            width="16" height="16" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round"
            className={`transition-transform duration-200 ${collapsed ? "rotate-180" : ""}`}
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      </div>

      {/* Nav items */}
      <nav className={`flex-1 py-4 space-y-1 ${collapsed ? "px-2" : "px-3"}`}>
        {NAV_ITEMS.map((item) => {
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              data-testid={`sidebar-nav-${item.id}`}
              title={collapsed ? item.label : undefined}
              aria-label={collapsed ? item.label : undefined}
              className={`w-full flex items-center rounded-xl text-sm font-medium transition-all duration-150 ${
                collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5"
              } ${
                isActive
                  ? "bg-forest/10 text-forest"
                  : "text-text-light hover:bg-secondary/50 hover:text-text"
              }`}
            >
              <svg
                width="20" height="20" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round"
              >
                <path d={item.icon} />
              </svg>
              {!collapsed && item.label}
            </button>
          );
        })}
      </nav>

      {/* Chat + Settings */}
      <div className={`pb-5 space-y-2 ${collapsed ? "px-2" : "px-3"}`}>
        <button
          onClick={onChatOpen}
          data-testid="desktop-chat-button"
          title={collapsed ? "Chat" : undefined}
          aria-label={collapsed ? "Chat" : undefined}
          className={`w-full flex items-center rounded-xl text-sm font-medium bg-forest text-white hover:bg-forest-med transition-colors ${
            collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5"
          }`}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {!collapsed && "Chat"}
        </button>
        <button
          onClick={onHelpOpen}
          data-testid="desktop-help-btn"
          title={collapsed ? "Help" : undefined}
          aria-label="Watch quick tours"
          className={`w-full flex items-center rounded-xl text-sm font-medium text-text-light hover:bg-secondary/50 hover:text-text transition-colors ${
            collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5"
          }`}
        >
          {HELP_ICON}
          {!collapsed && "Help"}
        </button>
        <button
          onClick={onSettingsOpen}
          data-testid="desktop-settings-btn"
          title={collapsed ? "Settings" : undefined}
          aria-label="Open settings"
          className={`w-full flex items-center rounded-xl text-sm font-medium text-text-light hover:bg-secondary/50 hover:text-text transition-colors ${
            collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5"
          }`}
        >
          {SETTINGS_ICON}
          {!collapsed && "Settings"}
        </button>
      </div>
    </aside>
  );
}
